import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'Src/components/Button/Button';
import { SketchPicker } from 'react-color';
import { SchoolHouse } from 'Src/models/house';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { checkAvailabilityHouseName } from 'Src/helpers/service/admin/houses';
import { AppUser } from 'Src/views/App/App';
import { uploadImage } from 'Src/helpers/service/image';
import { Loader } from 'Src/components/Loader/Loader';
import { generateColorLogo } from 'Src/helpers/utils/graphicsUtils';

interface Props {
  house?: SchoolHouse;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  user: AppUser;
}

interface State {
  isLoading: boolean;
  colors: any[];
  displayColorPicker: boolean;
  newColor: string;
  isLoadingPhoto: boolean;
  imagePreviewUrl: string;
}

export class HouseForm extends Component<Props, State> {
  private initialColors: string[];

  constructor(props) {
    super(props);

    const isHouseExist = this.isHouseExist();

    const { house } = this.props;

    this.initialColors = isHouseExist ? propz.get(house, ['colors'], []) : [];

    this.state = {
      isLoading: false,
      colors: [...this.initialColors],
      displayColorPicker: false,
      newColor: '#fff',
      isLoadingPhoto: false,
      imagePreviewUrl: isHouseExist ? propz.get(house, ['pic'], '') : '/dist/images/empty_pic_uploader_box.png'
    };
  }

  isHouseExist(): boolean {
    const { house } = this.props;

    return typeof house !== 'undefined';
  }

  acceptColor = () => {
    const newColors = this.state.colors;
    newColors.push(this.state.newColor);

    this.setState({ colors: newColors });
    this.handleClose();
  };

  handleClose = () => {
    this.setState({ displayColorPicker: false });
  };

  onColorChange = color => {
    this.setState({ newColor: color.hex });
  };

  onRemoveColor = (index: number): void => {
    const colors = [...this.state.colors];
    colors.splice(index, 1);

    this.setState({
      colors: colors
    });
  };

  addColorClick = () => {
    const newColors = this.state.colors;

    this.setState({
      colors: newColors,
      displayColorPicker: true
    });
  };

  private onChangePicClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files![0];
    if (!file) return;

    this.setState({
      isLoadingPhoto: true,
      imagePreviewUrl: URL.createObjectURL(file)
    });

    uploadImage(this.props.user, file)
      .then(({ key }) => {
        const picUrl = `${window.apiImg}/images/${key}`;
        setFieldValue('pic', picUrl);
        setFieldValue('picSource', 'user');
      })
      .then(() => {
        this.setState({ isLoadingPhoto: false });
      });
  };


  setHouseColor = (index: number, color: string): void => {
    const colors = [...this.state.colors];
    while (colors.length < 2) {
      colors.push(index === 0 ? '#227ebd' : '#7fb8e0');
    }
    colors[index] = color;
    this.setState({ colors });
  };

  renderColourPicker(label: string, index: number): React.ReactNode {
    const palette = ['#227ebd', '#7fb8e0', '#f08c1a', '#f7c07a', '#2f9e5a', '#8fd0a8', '#7b5bd6', '#b6a3ea', '#164170', '#da4453'];
    const value = this.state.colors[index] || (index === 0 ? '#227ebd' : '#7fb8e0');

    return (
      <div className="houseFormDs__colourGroup">
        <div className="houseFormDs__label">{label}</div>
        <div className="houseFormDs__swatches">
          {palette.map(color => (
            <button
              key={`${label}-${color}`}
              type="button"
              className={`houseFormDs__swatch ${value.toLowerCase() === color.toLowerCase() ? 'houseFormDs__swatch--selected' : ''}`}
              style={{ background: color }}
              onClick={() => this.setHouseColor(index, color)}
              aria-label={`${label} ${color}`}
            />
          ))}
          <label className="houseFormDs__customColour">
            <span>Custom</span>
            <input type="color" value={value} onChange={event => this.setHouseColor(index, event.target.value)} />
          </label>
        </div>
      </div>
    );
  }
  async onSubmit(values) {
    let picUrl = values.pic;
    let picSource = values.picSource;
    const colors = this.state.colors.length > 0 ? this.state.colors : ['#227ebd', '#7fb8e0'];
    const firstLetter = values.houseName.trim().charAt(0) || '';
    const user = this.props.user;

    const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
    const coloursChanged = !arraysEqual(colors, this.initialColors);

    const noColors = colors.length === 0;
    const autoMode = picSource === 'auto';
    const shouldGenerate = !picUrl || (autoMode && (coloursChanged || noColors));

    if (shouldGenerate) {
      const file = await generateColorLogo(colors, 200, 'house-logo.png', firstLetter);
      const { key } = await uploadImage(user, file);
      picUrl = `${window.apiImg}/images/${key}`;
      picSource = 'auto';
    }

    const dataCopy = {
      name: values.houseName.trim(),
      description: values.description,
      colors: colors,
      pic: picUrl,
      picSource: picSource
    };

    this.props.onSubmit(dataCopy);
  }

  render() {
    const { user } = this.props;
    const house = this.props.house;
    const houseSchema = Yup.object().shape({
      houseName: Yup.string()
        .required('Required')
        .test('houseName', 'You already have a house matching this name.', value => {
          const houseName = propz.get(house, ['name'], '');
          const isEqualName = houseName === value;

          switch (true) {
            case isEqualName:
              return true;
            case !value:
              return false;
            default:
              return checkAvailabilityHouseName(user, { name: value.trim() }).then(res => {
                return res.isAvailable;
              });
          }
        })
    });

    const colorNodes = this.state.colors.map((color, clrIndex) => {
      return (
        <div
          key={`color_index_${clrIndex}`}
          style={{ background: color }}
          className="eHouseForm_listItemColor mRemovable"
          onClick={() => this.onRemoveColor(clrIndex)}
        />
      );
    });

    const houseInitial = {
      houseName: propz.get(house, ['name'], ''),
      description: propz.get(house, ['description'], ''),
      pic: propz.get(house, ['pic'], ''),
      picSource: propz.get(house, ['picSource'], 'auto')
    };

    return (
      <Formik
        initialValues={houseInitial}
        validationSchema={houseSchema}
        onSubmit={values => {
          this.onSubmit(values);
        }}
        validateOnBlur={false}
        validateOnChange={false}>
        {({ touched, errors }) => (
          <Form className="houseFormDs">
            <Field name="pic">
              {({ form: { setFieldValue, values }, field: { value } }) => {
                const { isLoadingPhoto, imagePreviewUrl } = this.state;
                const displayUrl = imagePreviewUrl || value;
                return (
                  <div className="houseFormDs__crestBlock">
                    <div className="houseFormDs__crest">
                      {isLoadingPhoto ? (
                        <Loader />
                      ) : displayUrl ? (
                        <img src={displayUrl} alt="House crest preview" />
                      ) : (
                        <span>{propz.get(house, ['name'], '?').charAt(0) || '?'}</span>
                      )}
                    </div>
                    <div className="houseFormDs__crestActions">
                      <input
                        type="file"
                        name="pic"
                        id="houseLogo"
                        className="houseFormDs__file"
                        onChange={e => this.onChangePicClick(e, setFieldValue)}
                      />
                      <label htmlFor="houseLogo" className="schoolStructure-btn schoolStructure-btn--secondary">Replace crest</label>
                      {value && values.picSource === 'user' && (
                        <button
                          type="button"
                          className="schoolStructure-btn schoolStructure-btn--secondary"
                          onClick={() => {
                            setFieldValue('pic', '');
                            setFieldValue('picSource', 'auto');
                            this.setState({ imagePreviewUrl: '' });
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              }}
            </Field>
            <Field type="hidden" name="picSource" />

            <div className="schoolFormForm__grid">
              <div className="form-group schoolFormForm__field">
                <label>House name</label>
                <Field type="text" name="houseName" className="form-control" />
                {touched.houseName && errors.houseName ? (
                  <div className="invalid-feedback d-block">{errors.houseName}</div>
                ) : null}
              </div>
              <div className="form-group schoolFormForm__field">
                <label>Description</label>
                <Field component="textarea" name="description" className="form-control houseFormDs__textarea" />
              </div>
            </div>

            <div className="houseFormDs__colours">
              {this.renderColourPicker('Primary colour', 0)}
              {this.renderColourPicker('Secondary colour', 1)}
            </div>

            <div className="schoolFormForm__actions">
              <button type="button" className="schoolStructure-btn schoolStructure-btn--secondary" onClick={this.props.onCancel}>
                Cancel
              </button>
              <button type="submit" className="schoolStructure-btn schoolStructure-btn--primary">
                Save house
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}

